import moment from 'moment';
import type { Moment } from 'moment';
import { DatePicker, Input, Select } from 'antd';
import type { MergedRecord, OverrideableField, Overrides } from './types';
import styles from './MergedPreview.module.css';

interface MergedPreviewProps {
  merged: MergedRecord;
  overrides: Overrides;
  onOverrideChange: (field: OverrideableField, value: string) => void;
}

interface StaticRow {
  kind: 'static';
  label: string;
  value: string;
  preserveNewlines?: boolean;
}

interface EditableRow {
  kind: 'editable';
  label: string;
  field: OverrideableField;
  value: string;
}

type Row = StaticRow | EditableRow;

interface EditableFieldProps {
  row: EditableRow;
  onChange: (value: string) => void;
}

function EditableField({ row, onChange }: EditableFieldProps) {
  if (row.field === 'sex') {
    return (
      <Select
        value={row.value || undefined}
        onChange={(v) => onChange(v ?? '')}
        placeholder="Select sex"
        options={[
          { label: 'Male', value: 'MALE' },
          { label: 'Female', value: 'FEMALE' },
          { label: 'Other', value: 'OTHER' },
        ]}
        allowClear
        style={{ width: '100%' }}
      />
    );
  }
  if (row.field === 'dateOfBirth') {
    return (
      <DatePicker
        value={row.value ? moment(row.value) : undefined}
        onChange={(d: Moment | null) =>
          onChange(d ? d.format('YYYY-MM-DD') : '')
        }
        format="MMM D, YYYY"
        placeholder="Select DOB"
        style={{ width: '100%' }}
      />
    );
  }
  if (row.field === 'address' || row.field === 'notes') {
    const isNotes = row.field === 'notes';
    return (
      <Input.TextArea
        value={row.value}
        autoSize={{ minRows: 1, maxRows: isNotes ? 6 : 3 }}
        placeholder={`Add ${row.label.toLowerCase()}`}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <Input
      value={row.value}
      placeholder={`Add ${row.label.toLowerCase()}`}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function MergedPreview({
  merged,
  overrides,
  onOverrideChange,
}: MergedPreviewProps) {
  // Always editable; pre-populate with the Primary-Rule merged value so the
  // user sees the initial value and can override it.
  const overrideableRow = (
    label: string,
    field: OverrideableField,
    initial: string
  ): Row => ({
    kind: 'editable',
    label,
    field,
    value: overrides[field] ?? initial ?? '',
  });

  const rows: Row[] = [
    overrideableRow('Name', 'name', merged.fullName),
    overrideableRow('MRN', 'medicalRecordNumber', merged.medicalRecordNumber),
    overrideableRow('DOB', 'dateOfBirth', merged.dateOfBirth),
    overrideableRow('Sex', 'sex', merged.sex),
    overrideableRow(
      'Referring Physician',
      'referringPhysician',
      merged.referringPhysician
    ),
    overrideableRow('Address', 'address', merged.address),
    overrideableRow('Phone Number', 'phoneNumber', merged.phoneNumber),
    overrideableRow('Ethnicity', 'ethnicity', merged.ethnicity),
    overrideableRow('Notes', 'notes', merged.notes),
    {
      kind: 'static',
      label: 'Study Count',
      value: String(merged.studyCount),
    },
  ];

  return (
    <section className={styles.container} aria-label="Merged record preview">
      <header className={styles.header}>
        <span className={styles.title}>Merged Patient Record</span>
      </header>
      <dl className={styles.rows}>
        {rows.map((row) =>
          row.kind === 'static' ? (
            <div className={styles.row} key={row.label}>
              <dt className={styles.label}>{row.label}</dt>
              <dd
                className={`${styles.value} ${row.preserveNewlines ? styles.multiline : ''}`}
              >
                {row.value}
              </dd>
            </div>
          ) : (
            <div className={styles.row} key={row.label}>
              <dt className={styles.label}>{row.label}</dt>
              <dd className={styles.value}>
                <EditableField
                  row={row}
                  onChange={(v) => onOverrideChange(row.field, v)}
                />
              </dd>
            </div>
          )
        )}
      </dl>
    </section>
  );
}
